import './style.css';
import moment from "moment";
import ButtonPrimary from "../buttons/primary/primary";
import Loader from "../loader/loader";  
import FormField from "./form-field";
import SecondaryButton from "../buttons/secondary/secondary";
import RubricaModal from "../modals/rubrica/rubrica-modal";
import RubricasSection from "./components/form-rubrica-section";
import storage from "../../shared/services/storage";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useId, useState, useMemo } from "react";
import { getMyAccessService } from  "../../features/user/service/user-service";
import { User } from "../../shared/interfaces/user";
import { SEVERITY } from "../../shared/interfaces/alert";
import { useAlert } from "../alert/alert";
import { FormProps, FormTypes } from "../../shared/interfaces/form";
import MenuContext, { DISPLAY_TYPE } from '../menu/context/menu-context';
import { FormComponentProps } from './interface/form';
import { useFieldRulesProcessor, createMonetaryFieldsRules } from '../../shared/hooks/useFieldRule';
import { ProjectRubrica } from '../../shared/interfaces/rubrica';
import { toCents } from '../../shared/utils/money';

// Interface para seções
interface FormSection {
  title: string;
  icon: string;
  color: string;
  fields: FormProps[];
}

// Estender interface do Form
interface FormComponentPropsWithSections extends FormComponentProps {
  sections?: FormSection[];
}

export default function Form({ 
  data, 
  sections,
  title, 
  onSubmitForm, 
  onCancel, 
  isLoading = false, 
  isReadOnly = false,
  onClickedMenuContextType,
  showEdit = false,
  showFinancial = false,
  showModalFinancial = false,
  showDeleteIcon = false,
  showFinished = false,
  onFieldValueChange,
  onChangeCheckbox,
  fieldRules = [],
  rubricaTypes = [],
  pendingRubricas = [],
  onCreatePendingRubrica,
}: FormComponentPropsWithSections) {
  const baseId = useId();
  const { showAlert } = useAlert();
  const { 
    handleSubmit, 
    formState: { errors, isValid }, 
    reset, 
    control, 
    setValue, 
    getValues,
    watch,
    trigger,
  } = useForm({
    shouldUnregister: false,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  
  const [ user, setUser ] = useState<User>();
  const [ isEditor, setIsEditor ] = useState<boolean>(false);
  const [ isLocalLoading, setIsLocalLoading ] = useState<boolean>(false);
  const [ formFields, setFormFields ] = useState<FormProps[]>(data || []);
  
  // Estados para o modal de rubricas
  const [ isRubricaModalOpen, setIsRubricaModalOpen ] = useState<boolean>(false);
  const [ projectRubricas, setProjectRubricas ] = useState<ProjectRubrica[]>([]);
  const [ rubricasLoaded, setRubricasLoaded ] = useState<boolean>(false);

  // Verificar se deve renderizar em seções
  const shouldRenderSections = sections && sections.length > 0;

  const defaultMonetaryRules = useMemo(() => createMonetaryFieldsRules(
    'valor', 
    ['permanentMaterialValor', 'materialConsumptionValor', 'personalValor']
  ), []);

  const allRules = useMemo(() => [...defaultMonetaryRules, ...fieldRules], 
    [defaultMonetaryRules, fieldRules]);

  const { shouldFieldBeDisabled } = useFieldRulesProcessor(allRules);
  
  const isFinalLoading = isLoading || isLocalLoading;
  
  // Verificar se deve mostrar a seção de rubricas (apenas para projetos)
  const isProjectForm = useMemo(() => {
    // Verificar se é um formulário de projeto baseado nos campos ou no título
    const hasProjectFields = formFields.some(field => 
      ['principalInvestigatorId', 'financierId', 'duration', 'initialDate', 'finalDate'].includes(field.name)
    );
    
    const isProjectTitle = title.toLowerCase().includes('projeto') && 
                          !title.toLowerCase().includes('financeira') && 
                          !title.toLowerCase().includes('movimentação');
    
    return hasProjectFields || isProjectTitle;
  }, [formFields, title]);
  
  const isAddMode = title.includes('Adicionar');
  const isEditMode = title.includes('Editar');
  const shouldShowRubricaSection = (isAddMode || isEditMode) && isProjectForm;
  
  const updateFieldsDisabledStatus = useCallback(() => {
    const currentValues = getValues();
    
    setFormFields(prevFields => {
      if (!prevFields || !prevFields.length) return prevFields;
      
      return prevFields.map(field => {
        const shouldBeDisabled = shouldFieldBeDisabled(field.name, currentValues);
        
        if (Boolean(field.disabled) !== shouldBeDisabled) {
          return { ...field, disabled: shouldBeDisabled };
        }

        return field;
      });
    });
  }, [getValues, shouldFieldBeDisabled]);
  
  const calculateEndDate = useCallback(() => {
    const initialDateValue = getValues("initialDate");
    const duration = getValues("duration");
    
    if (!initialDateValue || !duration || isNaN(parseInt(duration))) {
      return;
    }
    
    try {
      const startDate = moment(initialDateValue);
      
      if (!startDate.isValid()) {
        return;
      }
      
      const endDate = startDate.clone().add(parseInt(duration), 'months');
      
      setValue('finalDate', endDate.toDate());
    } catch (error) {
      console.error('Erro ao calcular data final:', error);
    }
  }, [getValues, setValue]);

  const calculateDurationInMonths = useCallback(() => {
    const initialDateValue = getValues("initialDate");
    const finalDateValue = getValues("finalDate");
    
    if (!initialDateValue || !finalDateValue) {
      return;
    }
    
    try {
      const startDate = moment(initialDateValue);
      const endDate = moment(finalDateValue);
      
      if (!startDate.isValid() || !endDate.isValid()) {
        return;
      }
      
      const duration = endDate.diff(startDate, 'months');
      
      setValue('duration', duration);
    } catch (error) {
      console.error('Erro ao calcular duração em meses:', error);
    }
  }, [getValues, setValue]);

  // Função para calcular valor total das rubricas
  const calculateTotalFromRubricas = useCallback((rubricas: ProjectRubrica[]) => {
    return rubricas.reduce((total, rubrica) => total + Number(rubrica.valor || 0), 0);
  }, []);

  // Função para carregar rubricas do projeto existente
  const loadExistingRubricas = useCallback((defaultValues: Record<string, any>) => {
    if (rubricasLoaded || !shouldShowRubricaSection) return;
    
    if (isEditMode && defaultValues.rubricas) {
      try {
        const rubricasData = Array.isArray(defaultValues.rubricas) 
          ? defaultValues.rubricas 
          : [];

        const rubricas = rubricasData.map((r: any) => ({
          rubricaId: (r.id || r.rubricaId).toString(),
          valor: Number(r.valor || 0),
          rubrica: {
            id: (r.id || r.rubricaId).toString(),
            name: r.name || r.rubrica?.name || '',
            description: r.description || r.rubrica?.description || ''
          }
        }));

        setProjectRubricas(rubricas);
        setRubricasLoaded(true);
      } catch (error) {
        console.error('Erro ao carregar rubricas existentes:', error);
        showAlert('Erro ao carregar rubricas do projeto', SEVERITY.ERROR);
      }
    } else {
      setProjectRubricas([]);
      void trigger('valor');
      setRubricasLoaded(true);
    }
  }, [calculateTotalFromRubricas, isEditMode, rubricasLoaded, shouldShowRubricaSection, showAlert, trigger]);

  // Funções do modal de rubricas
  const handleOpenRubricaModal = () => {
    setIsRubricaModalOpen(true);
  };

  const handleCloseRubricaModal = () => {
    setIsRubricaModalOpen(false);
  };

  const handleSaveRubricas = useCallback((rubricas: ProjectRubrica[]) => {
    try {
      const validRubricas = rubricas.filter(rubrica => 
        rubrica.rubricaId && 
        rubrica.valor > 0 && 
        rubrica.rubrica?.name
      );

      if (validRubricas.length !== rubricas.length) {
        console.warn('Algumas rubricas foram filtradas por serem inválidas');
      }
      
      const currentProjectValue = Number(getValues('valor') || 0);
      const nextRubricasTotal = calculateTotalFromRubricas(validRubricas);

      if (nextRubricasTotal > currentProjectValue) {
        showAlert('O total das rubricas nao pode ser maior que o valor do projeto', SEVERITY.WARNING);
        return;
      }

      setValue('rubricas', validRubricas, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: false,
      });
      setProjectRubricas(validRubricas);
      void trigger('valor');
      
      showAlert('Rubricas atualizadas com sucesso', SEVERITY.SUCCESS);
    } catch (error) {
      console.error('Erro ao salvar rubricas no Form:', error);
      showAlert('Erro ao salvar rubricas', SEVERITY.ERROR);
    }
  }, [calculateTotalFromRubricas, getValues, setValue, showAlert, trigger]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };
  
  const handleDurationBlur = (e: any) => {
    calculateEndDate();
  };

  const handleDateChange = () => {
    calculateDurationInMonths();
  };
  
  const fetchMyacess = useCallback(async () => {
    const url = window.location.pathname;

    try {
      setIsLocalLoading(true);
      const userLogged = await storage.getStorage('user');
      const userData = JSON.parse(userLogged || '');

      const myAcess = await getMyAccessService(userData.id);

      myAcess?.role.verboseAcess.find((verbose: string) => (verbose === 'PUT' || verbose === 'POST') && !url.includes('list')) 
        ? setIsEditor(true) 
        : setIsEditor(false);

      setUser(myAcess);
    } catch (error) {
      showAlert('Erro ao carregar suas permissões', SEVERITY.ERROR);
    } finally {
      setIsLocalLoading(false);
    }
  }, [showAlert]);

  const onSubmit = (data: any) => {
    try {
      const formData = new FormData();

      if(!data?.accountabilitty) {
        formData.delete('accountabilitty');
      }

      Object.keys(data).forEach(key => {
        if (key === 'recipt' && data[key] instanceof File) {
          formData.append('recipt', data[key]);
        } else if (data[key] !== null && data[key] !== undefined) {
          if (typeof data[key] === 'object' && !(data[key] instanceof File)) {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });
              
      if(data?.initialDate && data?.finalDate) {
        const firstDate = moment(data.initialDate).toISOString();
        const secondDate = moment(data.finalDate).toISOString();
        
        data.initialDate = firstDate;
        data.finalDate = secondDate;
        
        if (shouldShowRubricaSection) {
          const normalizedRubricas = projectRubricas.map(rubrica => ({
            rubricaId: rubrica.rubricaId,
            valor: rubrica.valor,
            rubrica: rubrica.rubrica
          }));
          data.valor = toCents(data.valor);
          
          delete data.permanentMaterialValor;
          delete data.materialConsumptionValor;
          delete data.personalValor;
          
          data.rubricas = normalizedRubricas;
        } else {
          data.valor = toCents(data.valor);
        }
        
        if(!isEditor) data.companyId = user?.companyId;
      }

      onSubmitForm?.(data);
    } catch (error) {
      showAlert('Erro ao processar formulário', SEVERITY.ERROR);
    }
  }

  const projectValue = watch('valor');
  const projectValueAmount = Number(projectValue || 0);

  const handleCancel = () => {
    onCancel?.();
  };

  const handleCheckboxChange = useCallback((value: boolean, fieldName: string) => {
      onChangeCheckbox?.(value, fieldName);
  }, [onChangeCheckbox]);

  const notifyParentAboutFieldChange = useCallback((fieldName: string, value: any) => {
    if (onFieldValueChange) {
      setTimeout(() => {
        onFieldValueChange(fieldName, value);
      }, 0);
    }
  }, [onFieldValueChange]);

  // Renderizar campos de uma seção
  const renderSectionFields = (fields: FormProps[], sectionIndex: number) => {
    return fields.map((item, index) => (
      <FormField
        key={`section-${sectionIndex}-field-${index}-${baseId}`}
        item={item}
        index={index}
        baseId={baseId}
        errors={errors}
        control={control}
        isEditor={isEditor}
        isFinalLoading={isFinalLoading}
        handleDurationBlur={handleDurationBlur}
        handleDateChange={handleDateChange}
        handleCheckboxChange={handleCheckboxChange}
        setValue={setValue}
        getValues={getValues}
        rubricaTypes={rubricaTypes}
        pendingRubricas={pendingRubricas}
        onCreatePendingRubrica={onCreatePendingRubrica}
      />
    ));
  };

  // Renderizar campos normais (quando não há seções)
  const renderNormalFields = () => {
    return formFields?.map((item, index) => (
      <FormField
        key={`form-field-${index}-${baseId}`}
        item={item}
        index={index}
        baseId={baseId}
        errors={errors}
        control={control}
        isEditor={isEditor}
        isFinalLoading={isFinalLoading}
        handleDurationBlur={handleDurationBlur}
        handleDateChange={handleDateChange}
        handleCheckboxChange={handleCheckboxChange}
        setValue={setValue}
        getValues={getValues}
        rubricaTypes={rubricaTypes}
        pendingRubricas={pendingRubricas}
        onCreatePendingRubrica={onCreatePendingRubrica}
      />
    ));
  };

  useEffect(() => {
    const monetaryFields = ['valor', 'permanentMaterialValor', 'materialConsumptionValor', 'personalValor'];
    const subscription = watch((values, { name, type }) => {
      if (!name) {
        return;
      }

      if (monetaryFields.includes(name)) {
        updateFieldsDisabledStatus();
      }

      notifyParentAboutFieldChange(name, values[name]);
    });
    
    return () => subscription.unsubscribe();
  }, [watch, updateFieldsDisabledStatus, notifyParentAboutFieldChange]);

  useEffect(() => {
    if (data && data.length > 0) {
      setFormFields([...data]);
      
      setTimeout(() => {
        if(fieldRules.length > 0) {
          updateFieldsDisabledStatus();
        }
      }, 100);
    }
  }, [data, updateFieldsDisabledStatus]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const dataString = JSON.stringify(data);
    const currentDataString = JSON.stringify(formFields);

    if (dataString === currentDataString && rubricasLoaded) return;

    const currentValues = getValues();
    const hasUserInput = (name: string) => {
      const v = currentValues[name];
      return v !== undefined && v !== null && v !== "";
    };

    const defaultValues = data.reduce((acc, item) => {
      const fallback = item.typeOfField === FormTypes.DATE ? (item.value || null) : (item.value || "");
      acc[item.name] = hasUserInput(item.name) ? currentValues[item.name] : fallback;
      return acc;
    }, {} as Record<string, any>);

    reset(defaultValues);
    void trigger();
    fetchMyacess();
    
    if (shouldShowRubricaSection) {
      loadExistingRubricas(defaultValues);
    }
    
    setTimeout(() => {
      if (title.includes('Editar')) {
        calculateEndDate();
      }
    }, 300);
    
  }, [data, shouldShowRubricaSection]);

  return (
    <>
      <form
        className="container-form"
        onSubmit={(event) => {
          event.preventDefault();
        }}
      >
        {isLoading && (
          <div className="form-loader-overlay">
            <div className="form-loader-container">
              <Loader />
            </div>
          </div>
        )}
        <div className="container-form__container">
          <div className="container-form__header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="container-form__title">{title}</h2>
              <MenuContext 
                display={DISPLAY_TYPE.INLINE}
                showAdd={false}
                showEdit={showEdit}
                clickedEdit={onClickedMenuContextType ? () => onClickedMenuContextType('edit') : undefined}
                showFinancial={showFinancial}
                showFinancialModal={showModalFinancial}
                showDeleteIcon={showDeleteIcon}
                showFinished={showFinished}
                clickedFinancial={onClickedMenuContextType ? () => onClickedMenuContextType('financial') : undefined}
                clickedFinancialModal={onClickedMenuContextType ? () => onClickedMenuContextType('financialModal') : undefined}
                clickedDelete={onClickedMenuContextType ? () => onClickedMenuContextType('delete') : undefined}
                clickedFinished={onClickedMenuContextType ? () => onClickedMenuContextType('finished') : undefined}
              />
            </div>
          </div>

          {/* Renderizar seções se existirem, senão renderizar campos normais */}
          {shouldRenderSections ? (
            <div className="container-form__sections">
              {sections.map((section, sectionIndex) => (
                <div key={`section-${sectionIndex}`} className={`form-section form-section--${section.color}`}>
                  <div className="form-section__header">
                    <div className="form-section__title-container">
                      <h3 className="form-section__title">
                        {section.title}
                      </h3>
                    </div>
                  </div>
                  <div className="form-section__content">
                    <div className="container-form__fields">
                      {renderSectionFields(section.fields, sectionIndex)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="container-form__fields">
              {renderNormalFields()}
            </div>
          )}

          {/* Componente de Rubricas separado - APENAS para formulários de projeto */}
          {shouldShowRubricaSection && (
            <RubricasSection
              projectRubricas={projectRubricas}
              onOpenModal={handleOpenRubricaModal}
              isEditor={isEditor}
              isLoading={isFinalLoading}
              formatCurrency={formatCurrency}
              projectValue={projectValueAmount}
              calculateTotalFromRubricas={calculateTotalFromRubricas}
            />
          )}

          <div className="container-form__actions">
            <SecondaryButton  
              title="Cancelar"  
              onClick={handleCancel}
            />
            {user?.role?.verboseAcess?.includes('PUT') && !isReadOnly && (
              <ButtonPrimary  
                title={title.includes('Editar') ? 'Salvar' : 'Salvar'} 
                onClick={handleSubmit((data)=> onSubmit(data))}
                disabled={!isEditor || isFinalLoading || !isValid}
              />
            )}
          </div>
        </div>
      </form>

      {/* Modal de Rubricas - APENAS se deve mostrar seção de rubricas */}
      {shouldShowRubricaSection && isRubricaModalOpen && (
        <RubricaModal
          isOpen={isRubricaModalOpen}
          onClose={handleCloseRubricaModal}
          onSave={handleSaveRubricas}
          initialRubricas={[...projectRubricas]}
          isLoading={isFinalLoading}
          rubricaTypes={rubricaTypes}
          pendingRubricas={pendingRubricas}
          onCreatePendingRubrica={onCreatePendingRubrica}
        />
      )}
    </>
  );
}
